import type React from "react";
import { useState, useMemo } from "react";
import "./CoverExtensionWidthInput.scss";
import { useT } from "i18n";

const CoverExtensionWidthInput = ({
  min = 0,
  max = 100,
  step = 1,
  value: initialValue = 50,
  onChange,
  showMarkers = true,
  markerInterval,
}: {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  showMarkers?: boolean;
  markerInterval?: number;
}) => {
  const t = useT();
  const [value, setValue] = useState(initialValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  // Generate marker positions - show fewer markers but always include min and max
  const markers = useMemo(() => {
    if (!showMarkers) return [];

    const range = max - min;

    // Calculate a reasonable interval to show ~5-8 markers
    // If markerInterval is provided, use it; otherwise calculate based on range
    let interval: number;
    if (markerInterval) {
      interval = markerInterval;
    } else {
      // Calculate interval to show approximately 6-7 markers
      const targetMarkerCount = 6;
      interval = Math.ceil(range / targetMarkerCount);
      // Round to nearest 5 for cleaner numbers
      interval = Math.ceil(interval / 5) * 5;
      // Ensure interval is at least the step size
      interval = Math.max(interval, step);
    }

    const markerValues: number[] = [min]; // Always include min

    // Add intermediate markers
    for (let i = min + interval; i < max; i += interval) {
      markerValues.push(i);
    }

    // Always include max if it's not already included
    if (markerValues[markerValues.length - 1] !== max) {
      markerValues.push(max);
    }

    return markerValues;
  }, [min, max, step, showMarkers, markerInterval]);

  return (
    <div style={{ marginBottom: "60px" }}>
      <span className={`managed-select__copy`}>
        {t("component.feature.extendedStrapLength", "Extended Strap Length (Inches)")}
      </span>
      <div style={{ marginTop: 4, marginBottom: 6, fontWeight: 500 }}>
        {t("component.feature.length", "Length: ")}
        {value} {t("component.feature.inches", "inches")}
      </div>
      <div className={"cover-extension-slider-container"}>
        <input
          type={"range"}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className={"cover-extension-range-slider"}
        />
        {showMarkers && (
          <div className={"cover-extension-slider-markers"}>
            {markers.map((markerValue) => (
              <div
                key={markerValue}
                className={"cover-extension-slider-marker"}
                style={{
                  left: `${((markerValue - min) / (max - min)) * 100}%`,
                }}
              >
                <span className={"cover-extension-slider-marker-tick"} />
                <span className={"cover-extension-slider-marker-label"}>
                  {markerValue}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoverExtensionWidthInput;
